import type { HttpClient } from '../http.js';

/**
 * Tagada Rx — storefront module.
 *
 * Drives the post-checkout clinical case flow from the browser:
 *   1. Ask the API whether a product needs an Rx case.
 *   2. After the order is paid, submit the intake (patient + answers)
 *      with the `(orderId, checkoutToken)` ownership proof.
 *   3. Poll the case status from the thank-you page.
 *
 * PHI handling: the `patient` and `intakeAnswers` payloads are sent
 * straight to the Tagada API which forwards them to the clinical
 * network (e.g. MDI). Tagada NEVER persists PHI in its database.
 *
 * Auth model: there is no API key. Each route authorizes per-resource
 * (productId is public; case routes require the checkoutToken the
 * storefront already received when it bootstrapped the checkout).
 *
 * @example
 * ```ts
 * const { required } = await tagada.rx.isRequiredForProduct(productId);
 * if (required) {
 *   const intake = await collectIntakeFromUser();
 *   const res = await tagada.rx.submitCase({
 *     orderId, checkoutToken, productId,
 *     patient, intakeAnswers: intake,
 *   });
 *   router.push(`/thank-you?rxCase=${res.rxCaseId}`);
 * }
 * ```
 */
export type RxCaseStatus =
  | 'requested'
  | 'submitted'
  | 'in_review'
  | 'approved'
  | 'shipped'
  | 'delivered'
  | 'declined'
  | 'cancelled'
  | 'error';

export interface RxRequiredForProduct {
  required: boolean;
  clinicalNetworkId: string | null;
  externalOfferingId: string | null;
  requiresLabs: boolean;
  requiresIdVerification: boolean;
  isDeaControlled: boolean;
}

export interface RxStorefrontPatient {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  /** ISO date YYYY-MM-DD. */
  dateOfBirth: string;
  sexAtBirth: 'male' | 'female' | 'unspecified';
  address: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zip: string;
    /** ISO 3166-1 alpha-2 country code. */
    country: string;
  };
  /** Patient-level clinical fields (MDI Full API / Judd). */
  weight?: number | string;
  height?: number | string;
  allergies?: string;
  pregnancy?: boolean | string;
  currentMedications?: string;
  medicalConditions?: string;
  specialNecessities?: string;
  driverLicenseId?: string;
  introVideoId?: string;
}

export interface RxIntakeAnswer {
  questionId: string;
  value: string | number | boolean | string[];
  comment?: string;
  /** Exact question text shown to the patient (MDI case_questions.question). */
  question?: string;
  /** MDI answer type: string | number | boolean. */
  type?: string;
  important?: boolean;
  isCritical?: boolean;
  displayInPdf?: boolean;
  description?: string;
  label?: string;
  metadata?: string;
  /** Options presented for MCQ questions. */
  displayedOptions?: string[];
}

export interface RxClinicalQuestion {
  externalQuestionId: string;
  title: string;
  description: string | null;
  placeholder: string | null;
  type: string;
  required: boolean;
  order: number;
  options: { value: string; label: string }[];
}

export interface RxQuestionsForProduct {
  required: boolean;
  externalQuestionnaireId: string | null;
  questions: RxClinicalQuestion[];
}

export interface RxSubmitCaseInput {
  orderId: string;
  checkoutToken: string;
  productId: string;
  patient: RxStorefrontPatient;
  intakeAnswers: RxIntakeAnswer[];
  preferredPharmacyId?: string;
}

export interface RxSubmitCaseResult {
  rxCaseId: string;
  externalCaseId: string;
  status: 'submitted' | 'error';
  accrualAmountMinor: number;
  accrualCurrency: string;
}

export interface RxCaseSummary {
  id: string;
  status: RxCaseStatus;
  externalCaseId: string | null;
  lastShipmentShippedAt: string | null;
  createdAt: string;
}

export class RxModule {
  constructor(private readonly http: HttpClient) {}

  /**
   * Check whether a product requires an Rx case. Storefronts call this
   * before rendering the intake form so they only ask the patient
   * questions when really necessary.
   *
   * Read-only / public — does not require any ownership proof.
   */
  async isRequiredForProduct(
    productId: string,
    opts?: { clinicalNetworkId?: string },
  ): Promise<RxRequiredForProduct> {
    return this.http.get<RxRequiredForProduct>('/api/public/v1/rx/required-for-product', {
      params: {
        productId,
        clinicalNetworkId: opts?.clinicalNetworkId,
      },
    });
  }

  /**
   * Look up the Rx cases attached to an order. Storefronts poll this
   * on the thank-you page to render "Your prescription is being
   * reviewed" → "Shipped" transitions.
   */
  async getCasesForOrder(input: {
    orderId: string;
    checkoutToken: string;
  }): Promise<{ cases: RxCaseSummary[] }> {
    return this.http.post<{ cases: RxCaseSummary[] }>(
      '/api/public/v1/rx/cases/lookup',
      input,
    );
  }

  /**
   * Submit a fresh clinical case for a freshly-paid order. PHI is
   * forwarded to the clinical network and never persisted on
   * Tagada-side rows.
   */
  async submitCase(input: RxSubmitCaseInput): Promise<RxSubmitCaseResult> {
    return this.http.post<RxSubmitCaseResult>('/api/public/v1/rx/cases/submit', input);
  }

  /**
   * Fetch the network-authored intake questions for a product.
   * Empty `questions` means the storefront should use its local quiz.
   */
  async getQuestionsForProduct(
    productId: string,
    variantId?: string,
  ): Promise<RxQuestionsForProduct> {
    return this.http.get<RxQuestionsForProduct>('/api/public/v1/rx/questions-for-product', {
      params: variantId ? { productId, variantId } : { productId },
    });
  }
}
